import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { useConfig } from "../app/config";
import { activeCount, DownloadSummaryContext, useDownloadSummary } from "../downloads/useDownloadSummary";
import { EMPTY_QUERY, fromSearchParams, type SearchQuery, toSearchParams } from "../search/query";
import { useSession } from "../session";
import { useVideoSummaryState, VideoSummaryContext } from "../videos/shared";
import { Avatar } from "./Avatar";
import { CardSizeContext, useCardSizeState } from "./cardSize";
import { Icon, type IconName } from "./icons";
import { SearchBar } from "./SearchBar";
import { CountBadge, countLabel, currentPlace, isBrowsePath, Sidebar, useSidebar } from "./Sidebar";

/**
 * The frame every logged-in screen sits in (46 Spec 3.1): the skip link, the sidebar, the top bar with the
 * search bar, the content, and on phones the bottom tabs.
 * The search bar reads its conditions from the address and writes them back to it; on other screens it is
 * empty and searching goes to the list.
 */
export function AppShell() {
  const { client, state } = useSession();
  const { suggestDelayMs } = useConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const main = useRef<HTMLElement>(null);
  const background = (location.state as { background?: { pathname: string } } | null)?.background;
  const visiblePath =
    location.pathname.startsWith("/g/") && background ? background.pathname : location.pathname;
  const videoSection = visiblePath.startsWith("/videos");
  const watching = /^\/videos\/[0-9a-f-]{36}$/.test(visiblePath);
  const sidebar = useSidebar(watching);
  const videos = useVideoSummaryState();
  const [more, setMore] = useState(false);
  const cardSize = useCardSizeState();
  const downloads = useDownloadSummary();

  const browsing = isBrowsePath(location.pathname) || visiblePath === "/videos/list";
  const query = useMemo(
    () => (browsing ? fromSearchParams(new URLSearchParams(location.search)) : EMPTY_QUERY),
    [browsing, location.search],
  );

  const suggest = useCallback(
    async (q: string) => (await client.call("catalog.suggestTags", { query: { q, limit: 8 } })).items,
    [client],
  );
  const search = useCallback(
    (next: SearchQuery) =>
      navigate({ pathname: videoSection ? "/videos/list" : "/", search: toSearchParams(next).toString() }),
    [navigate, videoSection],
  );

  // "/" puts the cursor in the search bar, unless something is being typed or a window is open
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t instanceof HTMLElement && (t.closest("input, select, textarea, dialog") || t.isContentEditable))
        return;
      if (document.querySelector("dialog[open]")) return;
      e.preventDefault();
      input.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const user = state.user;
  if (!user) return null;
  const at = currentPlace(visiblePath);
  const tab = (to: string, icon: IconName, label: string, current: boolean, count = 0) => (
    <Link to={to} aria-current={current ? "page" : undefined} aria-label={countLabel(label, count)}>
      <Icon name={icon} />
      {label}
      <CountBadge count={count} />
    </Link>
  );

  return (
    <VideoSummaryContext.Provider value={videos}>
      <div
        className="shell"
        data-section={videoSection ? "video" : undefined}
        data-sidebar={sidebar.open ? "open" : "closed"}
        data-cards={cardSize.size}
      >
        {/* biome-ignore lint/a11y/useValidAnchor: a skip link is a link by convention; the router must not see a hash move */}
        <a
          className="skip"
          href="#main"
          onClick={(e) => {
            // a hash change would be a router move; focus is all this link is for
            e.preventDefault();
            main.current?.focus();
          }}
        >
          본문으로
        </a>
        <Sidebar
          state={sidebar}
          user={user}
          summary={downloads.summary}
          videoCount={videos.mine?.processing.count ?? 0}
        />
        {sidebar.overlaid && (
          // biome-ignore lint/a11y/noStaticElementInteractions: a pointer shortcut; the keyboard has Escape and the button
          // biome-ignore lint/a11y/useKeyWithClickEvents: same
          <div className="side-scrim" data-testid="sidebar-scrim" onClick={sidebar.close} />
        )}
        <div className="frame">
          <header className="top">
            <div className="top-phone">
              <Link className="logo" to="/">
                Volgo
              </Link>
              <Link className="top-me" to="/settings" aria-label={`설정 (${user.username})`}>
                <Avatar user={user} />
              </Link>
            </div>
            <SearchBar
              query={query}
              onChange={search}
              suggest={suggest}
              delayMs={suggestDelayMs}
              inputRef={input}
              video={videoSection}
            />
            {videoSection && (
              <nav className="video-top-tabs" aria-label="영상 메뉴">
                <Link to="/videos">추천</Link>
                <Link to="/videos/list?sort=new">새 영상</Link>
                <Link to="/videos/list?sort=popular">인기</Link>
              </nav>
            )}
            <div className="top-end">
              <Link className="btn btn-txt video-upload-link" to="/videos/upload" aria-label="올리기">
                <Icon name="upload" small />
                <span className="up-label">올리기</span>
              </Link>
            </div>
          </header>
          <main id="main" ref={main} className="main" tabIndex={-1}>
            <CardSizeContext.Provider value={cardSize}>
              <DownloadSummaryContext.Provider value={downloads}>
                <Outlet />
              </DownloadSummaryContext.Provider>
            </CardSizeContext.Provider>
          </main>
        </div>
        <nav className="dock-tabs" aria-label="하단 메뉴">
          {tab("/", "compass", "탐색", at.browse)}
          {tab("/videos", "play", "영상", at.videos)}
          {tab("/bookmarks", "bookmark", "북마크", at.bookmarks)}
          {tab("/downloads", "down", "다운로드", at.downloads, activeCount(downloads.summary))}
          <button type="button" aria-expanded={more} onClick={() => setMore(!more)}>
            <Icon name="menu" />
            더보기
          </button>
          {more && (
            <div className="dock-more">
              <Link to="/history" onClick={() => setMore(false)}>
                기록
              </Link>
              <Link to="/settings" onClick={() => setMore(false)}>
                설정
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setMore(false)}>
                  관리
                </Link>
              )}
            </div>
          )}
        </nav>
      </div>
    </VideoSummaryContext.Provider>
  );
}
